/**
 * Collection Filter Grid
 * Handles: collapsibles, dual range slider, view toggle,
 *          AJAX filtering, sort, per-page, client-side search,
 *          mobile drawer, browser back/forward, quick add to cart.
 */

(function () {
  'use strict';

  const section = document.querySelector('.cfg-section');
  if (!section) return;

  const sectionId = section.dataset.sectionId;
  if (!sectionId) return;

  // ── DOM refs ───────────────────────────────────────────────────────
  const grid         = section.querySelector('#cfg-product-grid');
  const loading      = section.querySelector('#cfg-loading');
  const overlay      = section.querySelector('#cfg-overlay');
  const sidebar      = section.querySelector('#cfg-sidebar');
  const mobileToggle = section.querySelector('#cfg-mobile-filter-toggle');
  const closeBtn     = section.querySelector('#cfg-sidebar-close');
  const sortDesktop  = section.querySelector('#cfg-sort-desktop');
  const sortMobile   = section.querySelector('#cfg-sort-mobile');
  const perPageEl    = section.querySelector('#cfg-per-page');
  const searchInput  = section.querySelector('#cfg-search-input');
  const rangeSlider  = section.querySelector('#cfg-range-slider');
  const activeBar    = section.querySelector('#cfg-active-filters');
  const clearAllBtn  = section.querySelector('#cfg-clear-all');

  // ── State ──────────────────────────────────────────────────────────
  let currentCols = parseInt(grid && grid.dataset.defaultCols, 10) || 4;
  let searchTimer = null;
  let priceTimer  = null;
  let isFetching  = false;

  // ── 1. COLLAPSIBLES ────────────────────────────────────────────────
  section.querySelectorAll('.cfg-collapsible').forEach(block => {
    const toggle = block.querySelector('.cfg-filter-toggle');
    if (!toggle) return;
    toggle.addEventListener('click', () => {
      const open = block.dataset.open === 'true';
      block.dataset.open = open ? 'false' : 'true';
      toggle.setAttribute('aria-expanded', String(!open));
    });
  });

  // ── 2. MOBILE SIDEBAR ──────────────────────────────────────────────
  function openSidebar() {
    if (!sidebar) return;
    sidebar.classList.add('is-open');
    overlay && overlay.classList.add('is-active');
    mobileToggle && mobileToggle.setAttribute('aria-expanded', 'true');
    // cfg-drawer-open lifts <main> above the sticky header (see CSS) so the drawer
    // isn't trapped below it, and locks scroll on both html + body for iOS.
    document.body.classList.add('cfg-drawer-open');
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  }

  function closeSidebar() {
    if (!sidebar) return;
    sidebar.classList.remove('is-open');
    overlay && overlay.classList.remove('is-active');
    mobileToggle && mobileToggle.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('cfg-drawer-open');
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
  }

  mobileToggle && mobileToggle.addEventListener('click', openSidebar);
  closeBtn     && closeBtn.addEventListener('click', closeSidebar);
  overlay      && overlay.addEventListener('click', closeSidebar);
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && sidebar && sidebar.classList.contains('is-open')) closeSidebar();
  });

  // ── 3. VIEW TOGGLE ─────────────────────────────────────────────────
  function setViewCols(cols) {
    if (!grid) return;
    currentCols = cols;
    grid.className = `cfg-grid cfg-grid--${cols}`;
    section.querySelectorAll('.cfg-view-btn').forEach(btn => {
      btn.classList.toggle('cfg-view-btn--active', parseInt(btn.dataset.cols, 10) === cols);
    });
    try { sessionStorage.setItem('cfg-cols-' + sectionId, String(cols)); } catch (_) {}
  }

  try {
    const saved = sessionStorage.getItem('cfg-cols-' + sectionId);
    setViewCols(saved ? parseInt(saved, 10) : currentCols);
  } catch (_) { setViewCols(currentCols); }

  section.querySelectorAll('.cfg-view-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const cols = parseInt(btn.dataset.cols, 10);
      if (cols) setViewCols(cols);
    });
  });

  // ── 4. AJAX FILTERING ──────────────────────────────────────────────
  function showLoading() {
    if (!loading) return;
    loading.classList.add('is-active');
    loading.removeAttribute('aria-hidden');
  }

  function hideLoading() {
    if (!loading) return;
    loading.classList.remove('is-active');
    loading.setAttribute('aria-hidden', 'true');
  }

  // Filters resolved in the browser against the rendered cards — these must never
  // reach Shopify as URL params. Storefront filtering only honours facets enabled
  // in Search & Discovery; an unrecognised one returns 0 products.
  const CLIENT_SIDE_FILTERS = ['filter.p.available', 'filter.p.tag', 'filter.p.vendor', 'filter.p.equipment'];

  // Vendor names are spelt inconsistently in the store ("SIT" vs "SIT Group";
  // "LaunchCatering" / "Launchcatering" / "LAUNCHCATERING LTD" / "LaunchCatering Ltd").
  // Normalising and matching either-contains-either merges those spellings into one
  // working filter without needing the product data cleaned up first.
  function normVendor(s) {
    return (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  function vendorMatches(cardVendor, filterValue) {
    const a = normVendor(cardVendor);
    const b = normVendor(filterValue);
    if (!a || !b) return false;
    if (a === b) return true;
    if (b.length < 3) return false;          // too short to match loosely
    return a.indexOf(b) === 0 || b.indexOf(a) === 0;
  }

  // Equipment ("what machine is this for") is spelt inconsistently across the store:
  // sometimes a tag ("Gas Griddle"), sometimes the product type ("Gas Cooker Parts"),
  // and often only in the title ("Thermopile for GF90 GF120 Gas Fryer"). Searching all
  // three as one haystack makes the filter work on today's data, un-normalised.
  function equipHaystack(card) {
    return [
      card.dataset.tags,
      card.dataset.productTitle,
      card.dataset.productType,
    ].join(' ').toLowerCase();
  }

  // Every equipment term offered in the sidebar, ticked or not. A part that matches
  // none of them fits no NAMED machine — in this catalogue that means it is a generic
  // part ("1500mm Thermocouple M9x1 for Commercial Gas Catering Equipment") that may
  // well fit the machine being filtered for. Hiding it would bury sellable stock and
  // tell the shopper we have nothing, so by default it stays visible.
  // Cached: countMatches() asks for this once per sidebar option. Dropped at the start
  // of refreshOptionCounts(), which every path that re-renders the sidebar calls.
  let equipTermsCache = null;
  function allEquipTerms() {
    if (!equipTermsCache) {
      equipTermsCache = Array.from(
        section.querySelectorAll('.cfg-filter-input[name="filter.p.equipment"]')
      ).map(inp => inp.value.toLowerCase().trim()).filter(Boolean);
    }
    return equipTermsCache;
  }

  const KEEP_UNIVERSAL = section.dataset.equipKeepUniversal !== 'false';

  function isUniversalPart(card, terms) {
    const hay = equipHaystack(card);
    return !terms.some(t => hay.includes(t));
  }

  function buildParams() {
    const params = new URLSearchParams(window.location.search);
    [...params.keys()].forEach(k => { if (k.startsWith('filter.')) params.delete(k); });

    section.querySelectorAll('.cfg-filter-input:checked').forEach(inp => {
      if (CLIENT_SIDE_FILTERS.includes(inp.name)) return; // handled client-side
      params.append(inp.name, inp.value);
    });

    if (rangeSlider) {
      const rMin     = rangeSlider.querySelector('.cfg-range-min');
      const rMax     = rangeSlider.querySelector('.cfg-range-max');
      const absMin   = parseFloat(rangeSlider.dataset.min) || 0;
      const absMax   = parseFloat(rangeSlider.dataset.max) || 999;
      const paramMin = rangeSlider.dataset.paramMin || 'filter.v.price.gte';
      const paramMax = rangeSlider.dataset.paramMax || 'filter.v.price.lte';
      const lo = parseFloat(rMin ? rMin.value : absMin);
      const hi = parseFloat(rMax ? rMax.value : absMax);
      if (lo > absMin) params.set(paramMin, lo.toFixed(2));
      if (hi < absMax) params.set(paramMax, hi.toFixed(2));
    }

    return params;
  }

  async function fetchProducts(url) {
    if (isFetching) return;
    isFetching = true;
    showLoading();
    closeSidebar();

    try {
      const sep = url.includes('?') ? '&' : '?';
      const res = await fetch(`${url}${sep}section_id=${sectionId}`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const doc     = new DOMParser().parseFromString(await res.text(), 'text/html');
      const newGrid = doc.getElementById('cfg-product-grid');

      if (newGrid && grid) {
        grid.innerHTML = newGrid.innerHTML;
        grid.className = `cfg-grid cfg-grid--${currentCols}`;
      }

      const oldPager = section.querySelector('.cfg-pagination');
      const newPager = doc.querySelector('.cfg-pagination');
      if (oldPager && newPager)  oldPager.replaceWith(newPager);
      else if (newPager && grid) grid.after(newPager);
      else if (oldPager)         oldPager.remove();

      refreshActiveChips();
      sortStockFirst();
      refreshOptionCounts();
      applyVisibility();
      if (typeof initWishlistFromLocalStorage === 'function') initWishlistFromLocalStorage();
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });

    } catch (err) {
      console.error('[CFG] fetch error:', err);
    } finally {
      isFetching = false;
      hideLoading();
    }
  }

  function applyFilters() {
    const params = buildParams();
    params.delete('page');
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({ cfgFilter: true }, '', newUrl);
    fetchProducts(newUrl);
  }

  // Real-time: update URL + filter in DOM instantly, no fetch
  function applyFiltersRealtime() {
    const params = new URLSearchParams(window.location.search);
    [...params.keys()].forEach(k => { if (k.startsWith('filter.')) params.delete(k); });

    section.querySelectorAll('.cfg-filter-input:checked').forEach(inp => {
      if (!CLIENT_SIDE_FILTERS.includes(inp.name)) params.append(inp.name, inp.value);
    });

    if (rangeSlider) {
      const rMin     = rangeSlider.querySelector('.cfg-range-min');
      const rMax     = rangeSlider.querySelector('.cfg-range-max');
      const absMin   = parseFloat(rangeSlider.dataset.min) || 0;
      const absMax   = parseFloat(rangeSlider.dataset.max) || 9999;
      const paramMin = rangeSlider.dataset.paramMin || 'filter.v.price.gte';
      const paramMax = rangeSlider.dataset.paramMax || 'filter.v.price.lte';
      const lo = parseFloat(rMin ? rMin.value : absMin);
      const hi = parseFloat(rMax ? rMax.value : absMax);
      if (lo > absMin) params.set(paramMin, lo.toFixed(2)); else params.delete(paramMin);
      if (hi < absMax) params.set(paramMax, hi.toFixed(2)); else params.delete(paramMax);
    }

    params.delete('page');
    window.history.pushState({ cfgFilter: true }, '', `${window.location.pathname}?${params.toString()}`);
    applyVisibility();
    refreshActiveChips();
  }

  section.querySelectorAll('.cfg-filter-input').forEach(inp => {
    // Stock / brand / manufacturer resolve instantly in the browser — no fetch, no reload
    if (CLIENT_SIDE_FILTERS.includes(inp.name)) {
      inp.addEventListener('change', () => { applyVisibility(); refreshActiveChips(); });
    } else {
      inp.addEventListener('change', applyFiltersRealtime);
    }
  });

  // ── 5. SORT ────────────────────────────────────────────────────────
  function handleSort(value) {
    const params = new URLSearchParams(window.location.search);
    params.set('sort_by', value);
    params.delete('page');
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, '', newUrl);
    fetchProducts(newUrl);
  }

  sortDesktop && sortDesktop.addEventListener('change', () => {
    if (sortMobile) sortMobile.value = sortDesktop.value;
    handleSort(sortDesktop.value);
  });
  sortMobile && sortMobile.addEventListener('change', () => {
    if (sortDesktop) sortDesktop.value = sortMobile.value;
    handleSort(sortMobile.value);
  });

  // ── 6. PER-PAGE ────────────────────────────────────────────────────
  perPageEl && perPageEl.addEventListener('change', () => {
    const selected = parseInt(perPageEl.value, 10);
    const loaded   = grid ? grid.querySelectorAll('.cfg-card').length : 0;
    try { sessionStorage.setItem('cfg-per-page-' + sectionId, perPageEl.value); } catch (_) {}

    if (selected > loaded) {
      // Need more products than currently in DOM — fetch from page 1
      const params = new URLSearchParams(window.location.search);
      params.delete('page');
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.pushState({}, '', newUrl);
      fetchProducts(newUrl);
    } else {
      sortStockFirst();
      refreshOptionCounts();
      applyVisibility();
    }
  });

  // ── 7. PRICE RANGE SLIDER ──────────────────────────────────────────
  if (rangeSlider) {
    const rMin     = rangeSlider.querySelector('.cfg-range-min');
    const rMax     = rangeSlider.querySelector('.cfg-range-max');
    const fill     = rangeSlider.querySelector('.cfg-range-fill');
    const pMin     = section.querySelector('#cfg-price-min');
    const pMax     = section.querySelector('#cfg-price-max');
    const absMin   = parseFloat(rangeSlider.dataset.min) || 0;
    const absMax   = parseFloat(rangeSlider.dataset.max) || 999;
    const paramMin = rangeSlider.dataset.paramMin || 'filter.v.price.gte';
    const paramMax = rangeSlider.dataset.paramMax || 'filter.v.price.lte';

    function updateFill() {
      if (!rMin || !rMax || !fill) return;
      const span = absMax - absMin;
      fill.style.left  = `${((parseFloat(rMin.value) - absMin) / span) * 100}%`;
      fill.style.right = `${((absMax - parseFloat(rMax.value)) / span) * 100}%`;
    }

    function syncTextBoxes() {
      if (pMin) pMin.value = rMin ? rMin.value : '';
      if (pMax) pMax.value = rMax ? rMax.value : '';
      updateFill();
    }

    rMin && rMin.addEventListener('input', () => {
      const lo = parseFloat(rMin.value), hi = parseFloat(rMax ? rMax.value : absMax);
      if (lo >= hi) rMin.value = hi - 1;
      syncTextBoxes();
      applyFiltersRealtime();
    });
    rMax && rMax.addEventListener('input', () => {
      const lo = parseFloat(rMin ? rMin.value : absMin), hi = parseFloat(rMax.value);
      if (hi <= lo) rMax.value = lo + 1;
      syncTextBoxes();
      applyFiltersRealtime();
    });

    pMin && pMin.addEventListener('input', () => {
      if (rMin) rMin.value = Math.max(absMin, Math.min(parseFloat(pMin.value) || absMin, absMax - 1));
      updateFill();
      clearTimeout(priceTimer);
      priceTimer = setTimeout(applyFiltersRealtime, 300);
    });
    pMax && pMax.addEventListener('input', () => {
      if (rMax) rMax.value = Math.max(absMin + 1, Math.min(parseFloat(pMax.value) || absMax, absMax));
      updateFill();
      clearTimeout(priceTimer);
      priceTimer = setTimeout(applyFiltersRealtime, 300);
    });

    updateFill();
  }

  // ── 8. STOCK-FIRST SORT ────────────────────────────────────────────
  function sortStockFirst() {
    if (!grid) return;
    const cards = Array.from(grid.querySelectorAll('.cfg-card'));
    cards.filter(c => c.dataset.available === 'true').forEach(c => grid.appendChild(c));
    cards.filter(c => c.dataset.available !== 'true').forEach(c => grid.appendChild(c));
  }

  // ── 9. CLIENT-SIDE VISIBILITY (search · availability · brand · manufacturer · price · per-page) ───
  function applyVisibility() {
    if (!grid) return;

    // Search
    const q = searchInput ? searchInput.value.trim().toLowerCase() : '';

    // Availability
    const wantIn    = section.querySelector('.cfg-filter-input[name="filter.p.available"][value="1"]');
    const wantOut   = section.querySelector('.cfg-filter-input[name="filter.p.available"][value="0"]');
    const filterIn  = wantIn  && wantIn.checked;
    const filterOut = wantOut && wantOut.checked;
    const anyAvail  = filterIn || filterOut;

    // Brand = "fits this equipment brand" → matched against product tags
    const checkedBrands = Array.from(
      section.querySelectorAll('.cfg-filter-input[name="filter.p.tag"]:checked')
    ).map(inp => inp.value.toLowerCase().trim());

    // Manufacturer = "who made the part" → matched against product vendor
    const checkedMfrs = Array.from(
      section.querySelectorAll('.cfg-filter-input[name="filter.p.vendor"]:checked')
    ).map(inp => inp.value.toLowerCase().trim());

    // Equipment = "what machine is this part for" → matched against tags + title + type
    const checkedEquip = Array.from(
      section.querySelectorAll('.cfg-filter-input[name="filter.p.equipment"]:checked')
    ).map(inp => inp.value.toLowerCase().trim());
    const equipTerms = allEquipTerms();

    // Price (stored in cents on card, slider value is £)
    let priceMin = 0, priceMax = Infinity;
    if (rangeSlider) {
      const rMin   = rangeSlider.querySelector('.cfg-range-min');
      const rMax   = rangeSlider.querySelector('.cfg-range-max');
      const absMax = parseFloat(rangeSlider.dataset.max) || 9999;
      priceMin = (parseFloat(rMin ? rMin.value : 0)) * 100;
      const hiVal  = parseFloat(rMax ? rMax.value : absMax);
      priceMax     = hiVal >= absMax ? Infinity : hiVal * 100;
    }

    const limit = parseInt(perPageEl ? perPageEl.value : 200, 10) || 200;

    let visible = 0;
    grid.querySelectorAll('.cfg-card').forEach(card => {
      const available  = card.dataset.available === 'true';
      const cardVendor = (card.dataset.vendor || '').toLowerCase();
      const cardTags   = (card.dataset.tags   || '').toLowerCase();
      const cardSku    = (card.dataset.sku    || '').toLowerCase();
      const cardPrice  = parseFloat(card.dataset.price || 0);

      // Search matches product title OR any variant SKU
      const matchSearch = !q || (card.dataset.productTitle || '').toLowerCase().includes(q)
                             || cardSku.includes(q);
      const matchAvail  = !anyAvail       || (filterIn && available) || (filterOut && !available);
      // Within a group: OR (any ticked brand). Across groups: AND.
      // Substring match — tags are written loosely ("Falcon spare parts", "Pitco 24F"),
      // so an exact match would miss most of them.
      const matchBrand  = !checkedBrands.length || checkedBrands.some(b => cardTags.includes(b));
      const matchMfr    = !checkedMfrs.length   || checkedMfrs.some(m => vendorMatches(cardVendor, m));
      const matchEquip  = !checkedEquip.length  || checkedEquip.some(e => equipHaystack(card).includes(e))
                          || (KEEP_UNIVERSAL && isUniversalPart(card, equipTerms));
      const matchPrice  = cardPrice >= priceMin  && (priceMax === Infinity || cardPrice <= priceMax);

      const show = matchSearch && matchAvail && matchBrand && matchMfr && matchEquip && matchPrice && visible < limit;
      if (show) visible++;
      card.style.display = show ? '' : 'none';
    });

    const countEl = section.querySelector('.cfg-product-count strong');
    if (countEl) countEl.textContent = visible;
  }

  // ── 9b. OPTION COUNTS ─────────────────────────────────────────────
  // Shows how many parts in THIS collection match each brand/manufacturer,
  // and greys out the ones with none — so you never tick a dead filter.
  function countMatches(cards, inp) {
    const val = inp.value.toLowerCase().trim();
    if (inp.name === 'filter.p.tag') {
      return cards.filter(c => (c.dataset.tags || '').toLowerCase().includes(val)).length;
    }
    if (inp.name === 'filter.p.vendor') {
      return cards.filter(c => vendorMatches(c.dataset.vendor, val)).length;
    }
    if (inp.name === 'filter.p.equipment') {
      const terms = allEquipTerms();
      return cards.filter(c =>
        equipHaystack(c).includes(val) ||
        (KEEP_UNIVERSAL && isUniversalPart(c, terms))
      ).length;
    }
    return 0;
  }

  function refreshOptionCounts() {
    if (!grid) return;
    equipTermsCache = null;
    const cards = Array.from(grid.querySelectorAll('.cfg-card'));

    // If not one part in this collection names an equipment, the facet can only ever
    // offer "everything" under every option — a count that promises a narrowing it
    // cannot deliver. Hide it rather than mislead. It reappears by itself once the
    // parts are tagged or titled with their machine.
    const equipBlock = section.querySelector('.cfg-filter-block--equipment');
    if (equipBlock) {
      const anyNamed = cards.some(c => !isUniversalPart(c, allEquipTerms()));
      equipBlock.hidden = !anyNamed;
      // Clear any tick on the way out, or it would go on filtering the grid invisibly.
      if (!anyNamed) {
        equipBlock.querySelectorAll('.cfg-filter-input:checked').forEach(i => { i.checked = false; });
      }
    }

    // Menu-driven image cards
    section.querySelectorAll('.cfg-cat-box--check').forEach(box => {
      const inp = box.querySelector('.cfg-cat-box__input');
      if (!inp) return;
      const n = countMatches(cards, inp);

      let countEl = box.querySelector('.cfg-cat-box__count');
      if (!countEl) {
        countEl = document.createElement('span');
        countEl.className = 'cfg-cat-box__count';
        box.insertBefore(countEl, box.querySelector('.cfg-cat-box__tick'));
      }
      countEl.textContent = n;
      box.classList.toggle('is-empty', n === 0);
      inp.disabled = (n === 0 && !inp.checked);
    });

    // Plain checkbox blocks (templates with no Brand/Manufacturer menu set)
    section.querySelectorAll('.cfg-logo-label').forEach(label => {
      const inp = label.querySelector('.cfg-filter-input');
      if (!inp) return;
      const n = countMatches(cards, inp);

      let countEl = label.querySelector('.cfg-logo-count');
      if (!countEl) {
        countEl = document.createElement('span');
        countEl.className = 'cfg-logo-count';
        label.appendChild(countEl);
      }
      countEl.textContent = n;
      label.classList.toggle('is-empty', n === 0);
      inp.disabled = (n === 0 && !inp.checked);
    });
  }

  searchInput && searchInput.addEventListener('input', () => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(applyVisibility, 180);
  });

  // ── 10. ACTIVE FILTER CHIPS ────────────────────────────────────────
  const CHIP_LABELS = {
    'filter.p.tag':       'Brand',
    'filter.p.vendor':    'Manufacturer',
    'filter.p.available': 'Stock',
    'filter.p.equipment': 'Equipment'
  };

  function addChip(label, value, onRemove) {
    const chip = document.createElement('span');
    chip.className = 'cfg-active-filter-chip';
    chip.innerHTML = `<span>${label}: ${value}</span><button class="cfg-chip-remove" aria-label="Remove filter ${value}">×</button>`;
    chip.querySelector('.cfg-chip-remove').addEventListener('click', onRemove);
    activeBar.appendChild(chip);
  }

  function refreshActiveChips() {
    if (!activeBar) return;
    activeBar.innerHTML = '';

    // Client-side filters: chip reflects the ticked box, removing it just unticks.
    section.querySelectorAll('.cfg-filter-input:checked').forEach(inp => {
      if (!CLIENT_SIDE_FILTERS.includes(inp.name)) return;
      if (inp.name === 'filter.p.available') return; // shown via its own control
      addChip(CHIP_LABELS[inp.name] || 'Filter', inp.dataset.label || inp.value, () => {
        inp.checked = false;
        applyVisibility();
        refreshActiveChips();
      });
    });

    // Server-side filters still live in the URL (price, and any Shopify facets).
    new URLSearchParams(window.location.search).forEach((value, key) => {
      if (!key.startsWith('filter.') || CLIENT_SIDE_FILTERS.includes(key)) return;
      addChip(key.split('.').pop().replace(/-/g, ' '), value, () => {
        const p    = new URLSearchParams(window.location.search);
        const rest = p.getAll(key).filter(v => v !== value);
        p.delete(key);
        rest.forEach(v => p.append(key, v));
        p.delete('page');
        const newUrl = `${window.location.pathname}?${p.toString()}`;
        window.history.pushState({}, '', newUrl);
        section.querySelectorAll(`.cfg-filter-input[name="${key}"][value="${value}"]`)
          .forEach(i => { i.checked = false; });
        fetchProducts(newUrl);
      });
    });
  }

  // ── 10b. CATEGORY: smooth AJAX navigation between collections (no full reload) ──
  async function fetchCollection(url) {
    if (isFetching) return;
    isFetching = true;
    if (grid) grid.classList.add('is-switching');
    closeSidebar();
    try {
      const clean = url.split('#')[0];
      const sep = clean.includes('?') ? '&' : '?';
      const res = await fetch(`${clean}${sep}section_id=${sectionId}`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const doc     = new DOMParser().parseFromString(await res.text(), 'text/html');
      const newGrid = doc.getElementById('cfg-product-grid');
      if (!newGrid) { window.location.href = url; return; } // target on a different template — hard nav

      if (grid) {
        grid.innerHTML = newGrid.innerHTML;
        grid.className  = `cfg-grid cfg-grid--${currentCols} is-switching`;
      }

      const oldPager = section.querySelector('.cfg-pagination');
      const newPager = doc.querySelector('.cfg-pagination');
      if (oldPager && newPager)  oldPager.replaceWith(newPager);
      else if (newPager && grid) grid.after(newPager);
      else if (oldPager)         oldPager.remove();

      // Refresh collection header + counts to the new collection
      ['.cfg-collection-title', '.cfg-collection-meta', '.cfg-collection-badge', '.cfg-product-count', '.cfg-price-limits'].forEach(sel => {
        const nn = doc.querySelector(sel), cn = section.querySelector(sel);
        if (nn && cn) cn.innerHTML = nn.innerHTML;
      });

      // Reset the price slider bounds to the new collection's range
      const nSlider = doc.getElementById('cfg-range-slider');
      if (nSlider && rangeSlider) {
        const newMax = parseFloat(nSlider.dataset.max) || 9999;
        rangeSlider.dataset.max = newMax;
        const rMin = rangeSlider.querySelector('.cfg-range-min');
        const rMax = rangeSlider.querySelector('.cfg-range-max');
        const pMin = section.querySelector('#cfg-price-min');
        const pMax = section.querySelector('#cfg-price-max');
        const fill = rangeSlider.querySelector('.cfg-range-fill');
        if (rMin) { rMin.max = newMax; rMin.value = 0; }
        if (rMax) { rMax.max = newMax; rMax.value = newMax; }
        if (pMin) { pMin.value = 0; }
        if (pMax) { pMax.max = newMax; pMax.value = newMax; pMax.placeholder = newMax; }
        if (fill) { fill.style.left = '0%'; fill.style.right = '0%'; }
      }

      refreshActiveChips();
      sortStockFirst();
      refreshOptionCounts();
      applyVisibility();
      if (typeof initWishlistFromLocalStorage === 'function') initWishlistFromLocalStorage();
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });

    } catch (err) {
      console.error('[CFG] collection nav error:', err);
      window.location.href = url; // never leave the shopper stuck — hard nav on failure
    } finally {
      isFetching = false;
      if (grid) grid.classList.remove('is-switching');
    }
  }

  section.addEventListener('click', e => {
    const box = e.target.closest('.cfg-cat-box, .cfg-cat-pill');
    if (!box) return;
    const href = box.getAttribute('href');
    if (!href || href === '#') return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return; // let new-tab etc. work
    e.preventDefault();
    if (box.classList.contains('is-active')) return;
    section.querySelectorAll('.cfg-cat-box, .cfg-cat-pill').forEach(a => a.classList.remove('is-active'));
    box.classList.add('is-active');
    if (searchInput) searchInput.value = '';
    section.querySelectorAll('.cfg-filter-input').forEach(i => { i.checked = false; });
    window.history.pushState({ cfgCollection: true }, '', href.split('#')[0]);
    fetchCollection(href);
  });

  // ── 11. BROWSER BACK / FORWARD ─────────────────────────────────────
  window.addEventListener('popstate', () => {
    fetchCollection(window.location.href);
    syncUIFromURL();
  });

  // ── 12. SYNC UI FROM URL ───────────────────────────────────────────
  function syncUIFromURL() {
    const params = new URLSearchParams(window.location.search);

    const sort = params.get('sort_by');
    if (sort) {
      if (sortDesktop) sortDesktop.value = sort;
      if (sortMobile)  sortMobile.value  = sort;
    }

    section.querySelectorAll('.cfg-filter-input').forEach(inp => {
      // Client-side filters aren't in the URL — leave their ticks alone
      if (CLIENT_SIDE_FILTERS.includes(inp.name)) return;
      inp.checked = params.getAll(inp.name).includes(inp.value);
    });

    if (rangeSlider) {
      const paramMin = rangeSlider.dataset.paramMin || 'filter.v.price.gte';
      const paramMax = rangeSlider.dataset.paramMax || 'filter.v.price.lte';
      const lo       = params.get(paramMin);
      const hi       = params.get(paramMax);
      const rMin     = rangeSlider.querySelector('.cfg-range-min');
      const rMax     = rangeSlider.querySelector('.cfg-range-max');
      const pMin     = section.querySelector('#cfg-price-min');
      const pMax     = section.querySelector('#cfg-price-max');
      if (lo && rMin) { rMin.value = lo; if (pMin) pMin.value = lo; }
      if (hi && rMax) { rMax.value = hi; if (pMax) pMax.value = hi; }
      const fill   = rangeSlider.querySelector('.cfg-range-fill');
      const absMin = parseFloat(rangeSlider.dataset.min) || 0;
      const absMax = parseFloat(rangeSlider.dataset.max) || 999;
      if (fill) {
        const span = absMax - absMin;
        fill.style.left  = `${((parseFloat(lo || absMin) - absMin) / span) * 100}%`;
        fill.style.right = `${((absMax - parseFloat(hi || absMax)) / span) * 100}%`;
      }
    }

    refreshActiveChips();
  }

  // ── 13. CLEAR ALL ──────────────────────────────────────────────────
  clearAllBtn && clearAllBtn.addEventListener('click', () => {
    section.querySelectorAll('.cfg-filter-input').forEach(inp => { inp.checked = false; });

    if (rangeSlider) {
      const rMin   = rangeSlider.querySelector('.cfg-range-min');
      const rMax   = rangeSlider.querySelector('.cfg-range-max');
      const pMin   = section.querySelector('#cfg-price-min');
      const pMax   = section.querySelector('#cfg-price-max');
      const fill   = rangeSlider.querySelector('.cfg-range-fill');
      const absMin = parseFloat(rangeSlider.dataset.min) || 0;
      const absMax = parseFloat(rangeSlider.dataset.max) || 999;
      if (rMin) rMin.value = absMin;
      if (rMax) rMax.value = absMax;
      if (pMin) pMin.value = absMin;
      if (pMax) pMax.value = absMax;
      if (fill) { fill.style.left = '0%'; fill.style.right = '0%'; }
    }

    const params = new URLSearchParams(window.location.search);
    [...params.keys()].forEach(k => { if (k.startsWith('filter.')) params.delete(k); });
    params.delete('page');
    const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    window.history.pushState({}, '', newUrl);
    fetchProducts(newUrl);
  });

  // ── INIT ───────────────────────────────────────────────────────────
  syncUIFromURL();
  try {
    const savedPerPage = sessionStorage.getItem('cfg-per-page-' + sectionId);
    if (savedPerPage && perPageEl) perPageEl.value = savedPerPage;
  } catch (_) {}
  sortStockFirst();
  refreshOptionCounts();
  applyVisibility();

  // ── 14. QUICK ADD TO CART ──────────────────────────────────────────
  function showToast(msg, type) {
    let toast = document.getElementById('cfg-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'cfg-toast';
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    toast.className = 'is-visible' + (type === 'error' ? ' is-error' : '');
    clearTimeout(toast._t);
    toast._t = setTimeout(() => { toast.classList.remove('is-visible'); }, 2800);
  }

  function refreshHeaderCartCount() {
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        document.querySelectorAll('[data-cart-count], .cart-count, .js-cart-count, .header__cart-count')
          .forEach(el => { el.textContent = cart.item_count; });
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart } }));
      })
      .catch(() => {});
  }

  async function ajaxAddToCart(variantId, btn, title, qty) {
    const quantity = Math.max(1, parseInt(qty, 10) || 1);
    if (btn) { btn.classList.add('is-loading'); btn.disabled = true; }
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: Number(variantId), quantity })
      });
      if (!res.ok) throw new Error();
      showToast('✓ ' + title + ' added to cart');
      refreshHeaderCartCount();
      if (btn) {
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        setTimeout(() => { btn.classList.remove('is-added'); btn.disabled = false; }, 2200);
      }
    } catch (_) {
      showToast('Could not add to cart — please try again.', 'error');
      if (btn) { btn.classList.remove('is-loading'); btn.disabled = false; }
    }
  }

  // Single handler for all card interactions
  section.addEventListener('click', e => {
    // Quantity stepper — minus
    const minusBtn = e.target.closest('.cfg-qty-minus');
    if (minusBtn) {
      const input = minusBtn.closest('.cfg-qty-row').querySelector('.cfg-qty-input');
      if (input) input.value = Math.max(1, parseInt(input.value, 10) - 1);
      return;
    }

    // Quantity stepper — plus
    const plusBtn = e.target.closest('.cfg-qty-plus');
    if (plusBtn) {
      const input = plusBtn.closest('.cfg-qty-row').querySelector('.cfg-qty-input');
      if (input) input.value = Math.min(999, parseInt(input.value, 10) + 1);
      return;
    }

    // Single-variant quick add
    const btn = e.target.closest('.cfg-quick-add');
    if (btn && !btn.disabled) {
      if (btn.dataset.preorder === 'true' && btn.dataset.productUrl) {
        window.location.href = btn.dataset.productUrl;
        return;
      }
      const qtyInput = btn.closest('.cfg-card-cta').querySelector('.cfg-qty-input');
      const qty = qtyInput ? qtyInput.value : 1;
      ajaxAddToCart(btn.dataset.variantId, btn, btn.dataset.productTitle || 'Product', qty);
      return;
    }

    // Option select only arms the Add to Basket button below it; it never adds on its own.
    // (Before this, clicking a variant *was* the add-to-cart, which nobody guesses.)
  });

  // A select fires `change`, not `click`, so it needs its own listener.
  section.addEventListener('change', e => {
    const sel = e.target.closest('.cfg-variant-select');
    if (!sel) return;
    const cta = sel.closest('.cfg-card-cta');
    if (!cta) return;
    const addBtn = cta.querySelector('.cfg-quick-add');
    if (!addBtn) return;
    const opt = sel.options[sel.selectedIndex];
    addBtn.dataset.variantId = sel.value;
    // A variant with no stock keeps the pre-order route via the product page.
    const sellable = opt && opt.dataset.available !== 'false';
    addBtn.classList.toggle('cfg-btn--preorder', !sellable);
    addBtn.classList.toggle('cfg-btn--primary', sellable);
    addBtn.dataset.preorder = sellable ? '' : 'true';
    const lbl = addBtn.querySelector('.cfg-btn-label');
    if (lbl) lbl.textContent = sellable ? (addBtn.dataset.labelAdd || 'Add to Basket') : 'Pre-Order';
  });

})();
